/**
 * React Query hooks for the work pipeline.
 *
 * Invalidation is deliberately BROAD after a mutation, because these mutations
 * have documented side effects across resources: creating a work order moves the
 * CRF `confirmed → in_work`; a failed test round moves the work order to
 * `testing_failed` AND writes a notification the bell must show; a deployment
 * moves both the work order and the CRF and REMOVES the card from the board.
 * Invalidating only the mutated resource would leave the screen telling a
 * half-true story until something else happened to refetch.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { CountryCode } from '../../domain/countries';
import {
  assignWorkOrder,
  fetchWorkBoard,
  fetchWorkFiles,
  fetchWorkOrderRecord,
  recordDeployment,
  recordTestRound,
  setPaymentStatus,
  signOff,
  uploadWorkFile,
  workKeys,
} from './api';
import type {
  AssignResultDto,
  DeploymentDto,
  MailStatus,
  PaymentStatus,
  SignoffAcceptanceBody,
  SignoffDto,
  SignoffType,
  TestResult,
  TestRoundDto,
  TestRoundPointInput,
  WorkBoardDto,
  WorkFileDto,
  WorkOrderRecordDto,
} from './api';

/** The board. `enabled` is threaded rather than the hook called conditionally. */
export function useWorkBoard(
  countryCode: CountryCode,
  enabled: boolean,
): UseQueryResult<WorkBoardDto> {
  return useQuery({
    queryKey: workKeys.board(countryCode),
    queryFn: ({ signal }) => fetchWorkBoard(countryCode, signal),
    enabled,
  });
}

/**
 * One work order, with its CRF and its testing history.
 *
 * `enabled` carries the `crf.view` gate: without it the API answers 403, and a
 * failed request rendered as an empty record would tell the operator the work
 * order is blank rather than that they may not read it.
 */
export function useWorkOrderRecord(
  workOrderId: string | null,
  enabled: boolean,
): UseQueryResult<WorkOrderRecordDto> {
  return useQuery({
    queryKey: workKeys.record(workOrderId ?? ''),
    queryFn: ({ signal }) => fetchWorkOrderRecord(workOrderId ?? '', signal),
    enabled: enabled && workOrderId !== null,
  });
}

export function useWorkFiles(
  workOrderId: string,
  enabled: boolean,
): UseQueryResult<WorkFileDto[]> {
  return useQuery({
    queryKey: workKeys.files(workOrderId),
    queryFn: ({ signal }) => fetchWorkFiles(workOrderId, signal),
    enabled,
  });
}

/**
 * One invalidator, awaited in every `onSuccess`.
 *
 * Awaited, not fired-and-forgotten: the mutation is not "done" until the screen
 * can show the new truth, and returning early makes a spinner stop over stale
 * numbers — the failure the quotes feature already learned.
 */
function useWorkInvalidation(countryCode: CountryCode): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: workKeys.board(countryCode) }),
      // The bell: assign and test-return are live notification wire-ups, so a
      // pipeline mutation can change the unread count.
      queryClient.invalidateQueries({ queryKey: ['notifications'] }),
      // CRF and work-order records carry statuses these mutations move.
      //
      // ⚠ THIS PREFIX WAS `['crf']` AND MATCHED NOTHING. Every CRF query in this app is
      // keyed under `'crfs'` — `crfKeys.record` is `['crfs', 'record', id]`, the list is
      // `['crfs', cc, 'list', …]` (`features/crfs/api.ts`) — and React Query's partial
      // match compares elements, so `'crf'` never equalled `'crfs'`. The line was written
      // (176ccc0, U5 slice 1) before the CRF feature declared any keys at all
      // (39c48bb, W0), and nobody reconciled the two. It went unnoticed for as long as the
      // only screen calling these hooks read the CRF through the WORK-ORDER record
      // (`['work-order', id]`, refetched below); the moment `PaymentPanel` was mounted on
      // the CRF RECORD (Train E, fork 15) the gap became visible — the card would toast
      // "Payment status is now Received" while `crfKeys.record` kept serving "Pending".
      // `use-work.test.tsx` pins the refetch, both arms (a live `crfKeys.record` query
      // refetches; an unrelated live query does not); the plant that reddens it is this
      // one word.
      queryClient.invalidateQueries({ queryKey: ['crfs'] }),
      // The record surface itself: an assignment, a test round and a deployment
      // each change what it shows. Invalidating the board but not the record is
      // how the screen the operator is LOOKING AT becomes the stale one.
      queryClient.invalidateQueries({ queryKey: ['work-order'] }),
    ]);
  };
}

export function useAssignWork(countryCode: CountryCode) {
  const invalidate = useWorkInvalidation(countryCode);
  return useMutation<
    AssignResultDto,
    unknown,
    { workOrderId: string; userId: string; tallySerial?: string }
  >({
    // v22-E2: the serial rides the same mutation — one act, one request.
    mutationFn: ({ workOrderId, userId, tallySerial }) =>
      assignWorkOrder(workOrderId, userId, tallySerial),
    onSuccess: invalidate,
  });
}

/**
 * Record a round. `points` carries the per-point verdicts when the tester judged any.
 *
 * The broad invalidation above matters MORE here than anywhere else in this file:
 * `coverage` is recomputed server-side on every read of the record, so a round that
 * discharges the last outstanding point changes what the deploy panel says. Without
 * `['work-order']` in the invalidator the tester would record the verdict and go on
 * reading a warning that is no longer true.
 */
export function useRecordRound(countryCode: CountryCode) {
  const invalidate = useWorkInvalidation(countryCode);
  return useMutation<
    TestRoundDto,
    unknown,
    {
      workOrderId: string;
      result: TestResult;
      note?: string | null;
      points?: TestRoundPointInput[];
    }
  >({
    mutationFn: ({ workOrderId, result, note, points }) =>
      recordTestRound(workOrderId, { result, note, points }),
    onSuccess: invalidate,
  });
}

export function useRecordDeployment(countryCode: CountryCode) {
  const invalidate = useWorkInvalidation(countryCode);
  return useMutation<
    DeploymentDto,
    unknown,
    {
      workOrderId: string;
      kind?: 'deploy' | 'redeploy';
      /** Migration 62 — unstated stays ABSENT on the wire; the server stores NULL. */
      mode?: 'online' | 'offline';
      due_date?: string;
      note?: string | null;
    }
  >({
    mutationFn: ({ workOrderId, kind, mode, due_date, note }) =>
      recordDeployment(workOrderId, { kind, mode, due_date, note }),
    onSuccess: invalidate,
  });
}

/**
 * What the panel STATES about the customer's acceptance on a PROJECT sign-off (F31).
 * `{ kind: 'document', attachmentId }` names a file attached to this CRF; `{ kind:
 * 'not_required', reason }` is fork 5's hatch, reason INCLUDED AS TYPED — blank and all —
 * because the server is the one that refuses a blank reason (400 naming
 * `not_required_reason`), and a client that pre-empted that would be asserting the rule
 * instead of proving it holds. `{ kind: 'unstated' }` sends none of the three, and the
 * server answers 409 naming the acceptance: that refusal is the panel's promise, and it
 * has to be the SERVER's sentence on screen, not this hook's.
 */
export type AcceptanceStatement =
  | { kind: 'unstated' }
  | { kind: 'document'; attachmentId: string }
  | { kind: 'not_required'; reason: string };

/**
 * The wire body for one statement — pure, exported so the test can pin the mapping
 * without a rendered panel between it and the assertion.
 *
 * ⚠ THE DEVELOPER ROUTE GETS `{}` FROM THIS, WHATEVER WAS STATED. The API refuses the
 * three acceptance fields there (400 `wrong_signoff`) rather than ignoring them, so a
 * hook that forwarded them would turn every developer signature into a refusal the
 * moment the panel held a picked document. The acceptance belongs to the PROJECT
 * sign-off (fork 1: the second stage, on every job).
 */
export function acceptanceBody(
  type: SignoffType,
  statement: AcceptanceStatement,
): SignoffAcceptanceBody {
  if (type === 'developer') return {};
  switch (statement.kind) {
    case 'document':
      return { acceptance_attachment_id: statement.attachmentId };
    case 'not_required':
      return { not_required: true, not_required_reason: statement.reason };
    case 'unstated':
      return {};
  }
}

/**
 * THE BACK CATALOGUE, on the row the panel has (fork 21, F32): a SIGNED PROJECT sign-off that
 * cites no document and states no waiver — pure, exported so the test can pin the cells.
 *
 * This is the API chase list's predicate restated (`registers.service.ts`,
 * `acceptanceChase`): `type = 'project' AND status = 'signed' AND acceptance_attachment_id IS
 * NULL AND COALESCE(not_required, 0) = 0`. `not_required !== true` is the COALESCE — a NULL
 * flag on a row closed before migration 50 and a `false` on a hand-nulled document close both
 * mean "no waiver stated", and both are rows whose acceptance is still to be chased.
 * ⚠ `type === 'project'` IS LOAD-BEARING: a DEVELOPER row is all-null BY DESIGN (fork 1 — the
 * acceptance is the second stage's), and it must never read as "missing acceptance".
 */
export function isAcceptanceUnrecorded(row: WorkOrderRecordDto['signoffs'][number]): boolean {
  return (
    row.type === 'project' &&
    row.status === 'signed' &&
    row.acceptance_attachment_id === null &&
    row.not_required !== true
  );
}

export function useSignOff(countryCode: CountryCode) {
  const invalidate = useWorkInvalidation(countryCode);
  return useMutation<
    SignoffDto,
    unknown,
    {
      crfId: string;
      type: SignoffType;
      mailStatus?: MailStatus;
      /** Absent reads as `unstated` — the developer route never states one. */
      acceptance?: AcceptanceStatement;
    }
  >({
    mutationFn: ({ crfId, type, mailStatus, acceptance }) => {
      const stated = acceptanceBody(type, acceptance ?? { kind: 'unstated' });
      // NAMED PROPERTIES, NOT A SPREAD — deliberately. TypeScript excess-property
      // checks apply to properties written out in a literal and NOT to spread
      // ones, so `{ mail_status, ...stated }` would still compile the day somebody
      // dropped the acceptance fields from `signOff`'s body type, and the client
      // would go on sending fields its own contract no longer declared. Written
      // out, dropping a field from `SignoffAcceptanceBody` reddens THIS line. An
      // undefined value never reaches the wire: `apiRequest` JSON-serialises the
      // body and JSON has no undefined.
      return signOff(crfId, type, {
        mail_status: mailStatus,
        acceptance_attachment_id: stated.acceptance_attachment_id,
        not_required: stated.not_required,
        not_required_reason: stated.not_required_reason,
      });
    },
    onSuccess: invalidate,
  });
}

export function useSetPaymentStatus(countryCode: CountryCode) {
  const invalidate = useWorkInvalidation(countryCode);
  return useMutation<
    { id: string; payment_status: PaymentStatus },
    unknown,
    { crfId: string; paymentStatus: PaymentStatus }
  >({
    mutationFn: ({ crfId, paymentStatus }) => setPaymentStatus(crfId, paymentStatus),
    onSuccess: invalidate,
  });
}

export function useUploadWorkFile(workOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation<WorkFileDto, unknown, File>({
    mutationFn: (file) => uploadWorkFile(workOrderId, file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: workKeys.files(workOrderId) });
    },
  });
}
